import { format, parseISO } from 'date-fns'

import { emotionTheme } from '@styles/theme'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { PreviewStatusHistoryItemProps } from '@services/api/previewManagement/types'

import * as S from '../styles'

const formatDate = (value: string | null | undefined, pattern: string) => (value ? format(parseISO(value), pattern) : '–')

// Linha de conta traz accountStatus; linha de GP (sem conta) traz só grossProfitStatus
export const getHistoryStatus = (row: PreviewStatusHistoryItemProps) => row.accountStatus ?? row.grossProfitStatus

// Envio automático vem sem responsibleUser — mostra o perfil ("Sistema")
const getUserName = (row: PreviewStatusHistoryItemProps) => row.responsibleUser?.name ?? row.userProfile?.name ?? '–'

const getInitials = (name: string) =>
  (name ?? '')
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('')

const columns: ColumnDefProps[] = [
  { id: 'referenceDate', columnName: 'Data Competência', value: '', align: 'center', minWidth: '10rem', sortable: true, sortKey: 'referenceDate' },
  { id: 'weekNumber', columnName: 'Semana', value: '', align: 'center', minWidth: '13rem', sortable: true, sortKey: 'weekNumber' },
  { id: 'branch', columnName: 'Centro de Lucro', value: '', headerAlign: 'center', bodyAlign: 'left', minWidth: '9rem', sortable: true, sortKey: 'branchCode' },
  { id: 'status', columnName: 'Status', value: '', align: 'center', minWidth: '11rem', sortable: true, sortKey: 'status' },
  { id: 'isAutoSend', columnName: 'Envio Automático', value: '', align: 'center', minWidth: '10rem', sortable: true, sortKey: 'isAutomaticSend' },
  { id: 'user', columnName: 'Usuário', value: '', headerAlign: 'center', bodyAlign: 'left', minWidth: '11rem', sortable: true, sortKey: 'userName' },
  { id: 'profile', columnName: 'Perfil', value: '', align: 'center', minWidth: '11rem', sortable: true, sortKey: 'userProfile' },
  { id: 'updatedAt', columnName: 'Data Atualização', value: '', align: 'center', minWidth: '11rem', sortable: true, sortKey: 'createdAt' }
]

export const parsePreviewHistoryGrid = (rows: PreviewStatusHistoryItemProps[] = [], totalItems: number) => {
  const data: ColumnDefProps[][] = rows.map((row) => [
    { ...columns[0], value: formatDate(row.referenceDate, 'dd/MM/yyyy') },
    { ...columns[1], value: `Semana ${row.weekNumber}` },
    {
      ...columns[2],
      value: (
        <S.CellStack>
          <S.CellTitle>{row.branchCode}</S.CellTitle>
          <S.CellSubtitle>{row.branchName}</S.CellSubtitle>
        </S.CellStack>
      ),
      withoutTooltip: true
    },
    { ...columns[3], value: getHistoryStatus(row) ? <BudgetPreviewsStatus status={getHistoryStatus(row)!} compact /> : '–', withoutTooltip: true },
    {
      ...columns[4],
      value: <S.AutoSendPill isAutoSend={row.isAutomaticSend}>{row.isAutomaticSend ? 'Sim' : 'Não'}</S.AutoSendPill>,
      withoutTooltip: true
    },
    {
      ...columns[5],
      value: (
        <S.UserInfo>
          <S.Avatar>{getInitials(getUserName(row))}</S.Avatar>
          <S.CellStack>
            <S.CellTitle>{getUserName(row)}</S.CellTitle>
            {!!row.responsibleUser?.email && <S.CellSubtitle>{row.responsibleUser.email}</S.CellSubtitle>}
          </S.CellStack>
        </S.UserInfo>
      ),
      withoutTooltip: true
    },
    { ...columns[6], value: row.userProfile?.name ?? '–' },
    { ...columns[7], value: formatDate(row.createdAt, 'dd/MM/yyyy HH:mm:ss') }
  ])

  const totalData: ColumnDefProps[][] = rows.length
    ? [
        [
          {
            id: 'totalRecords',
            columnName: '',
            colSpan: columns.length,
            columnColor: emotionTheme.colors.gray[100],
            withoutTooltip: true,
            value: <b>Total de registros: {totalItems.toLocaleString('pt-BR')}</b>
          }
        ]
      ]
    : []

  return { data, totalData }
}
